import { translate } from '@jsverse/transloco';

import { MatchmakerHandler } from '../../../../../../../providers/semantria-api/spote.service';
import {
  MatchmakerHandlerValidationResult,
  WidgetAiInsightsValidatorService,
} from './widget-ai-insights-validator.service';

export type RatingScoring = 'AVG' | 'AVERAGE' | 'SUM' | 'BOX' | 'NPS' | 'RATING' | 'N/A';

interface ChartProperties {
  isTimeSeries: boolean;
  isMultipleDocuments: boolean;
  isRating: boolean;
  ratingScoring?: RatingScoring[];
}

export class ChartPropertyValidator implements ChartProperties {
  constructor(
    private validatorService: WidgetAiInsightsValidatorService,
    public isTimeSeries: boolean,
    public isMultipleDocuments: boolean,
    public isRating: boolean,
    public ratingScoring?: RatingScoring[]
  ) {}

  validateHandler(handler: MatchmakerHandler): MatchmakerHandlerValidationResult {
    if (this.isMultipleDocuments && !this.validatorService.isDocCardinalityMultiple(handler)) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.DOC_CARDINALTY.single'),
      };
    }
    if (!this.isMultipleDocuments && this.validatorService.isDocCardinalityMultiple(handler)) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.DOC_CARDINALTY.multiple'),
      };
    }
    if (!this.isTimeSeries && this.validatorService.isTimeSeriesRequired(handler)) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.TIME_SERIES_REQUIRED'),
      };
    }
    if (!this.isRating && this.validatorService.isRatingRequired(handler)) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.RATING_REQUIRED'),
      };
    }
    const handlerRatingScoring = this.validatorService.getHandlerPropertyList(
      handler,
      'RATING_CALCULATION'
    ) as RatingScoring[];
    if (
      handlerRatingScoring.length &&
      handlerRatingScoring[0] !== 'N/A' &&
      !handlerRatingScoring.some((hrs) => this.ratingScoring?.includes(hrs))
    ) {
      return {
        handler,
        disabled: true,
        reason: translate('widgets.ai-insights.handler-tooltips.RATING_CALCULATION', {
          scoring: handlerRatingScoring,
        }),
      };
    }

    return {
      handler,
      disabled: false,
    };
  }
}

export class ChartPropertyValidatorBuilder implements ChartProperties {
  isTimeSeries: boolean;
  isMultipleDocuments: boolean = true;
  isRating: boolean;
  ratingScoring: RatingScoring[];

  withTimeSeries(isTimeSeries) {
    this.isTimeSeries = isTimeSeries;
    return this;
  }

  withMultipleDocuments(isMultipleDocuments) {
    this.isMultipleDocuments = isMultipleDocuments;
    return this;
  }

  withRating(isRating) {
    this.isRating = isRating;
    return this;
  }

  withRatingScoring(...scoring: string[]) {
    this.ratingScoring = scoring.flatMap((s) => [s?.toLocaleUpperCase() as RatingScoring]);
    return this;
  }

  build(validatorService: WidgetAiInsightsValidatorService) {
    const validator = new ChartPropertyValidator(
      validatorService,
      this.isTimeSeries,
      this.isMultipleDocuments,
      this.isRating
    );
    if (this.ratingScoring) {
      validator.ratingScoring = this.ratingScoring;
    }
    return validator;
  }
}
